"use client";

/**
 * SearchMock — animated mock of the askFinz Search engine for /apps/search.
 *
 * Pure presentation. Pre-typed query bar; result cards stream in with
 * confidence bars, provenance domains, snippet, one badged with
 * "[Cited by Researcher · 2 mins ago]". Bottom: "Built for LLMs and
 * humans · structured output available."
 *
 * NOT the real search surface (search.askfinz.ai/app/components/SearchView.tsx
 * is 3k+ lines with Qdrant fetches). Lives in askfinz.com only.
 */
import { useEffect, useRef, useState } from "react";
import {
  Search,
  ShieldCheck,
  ShieldAlert,
  Sparkles,
  Bot,
  Code2,
  User,
  ArrowUpRight,
  Globe,
  FileJson,
  ChevronRight,
} from "lucide-react";
import { COLLECTION_COUNT } from "@/lib/apps-registry";

type Result = {
  id: string;
  title: string;
  domain: string;
  url: string;
  snippet: string;
  /** 0..1 — drives the confidence bar */
  confidence: number;
  provenance: string[];
  citedBy?: { who: string; ago: string };
  kind: "intergov" | "press" | "primary" | "analysis";
};

const QUERY = "EU AI Act risk tier 2 obligations";

const RESULTS: Result[] = [
  {
    id: "r-1",
    title: "EU AI Act · Article 9 · Risk management system for tier-2 systems",
    domain: "eur-lex.europa.eu",
    url: "eur-lex.europa.eu › legal-content › AI-Act › art-9",
    snippet:
      "Providers of high-risk AI systems shall establish, implement, document and maintain a risk-management system as a continuous iterative process planned and run throughout the entire lifecycle of the system…",
    confidence: 0.94,
    provenance: ["eur-lex.europa.eu", "primary"],
    kind: "primary",
  },
  {
    id: "r-2",
    title: "Tier-2 obligations explainer · what shipped, what's deferred",
    domain: "askfinz.ai",
    url: "askfinz.ai › research › ai-act-tier2",
    snippet:
      "Conformity assessments, post-market monitoring and incident reporting all move into scope. The General-Purpose-AI obligations sit on a separate schedule and are not part of the tier-2 package…",
    confidence: 0.86,
    provenance: ["askfinz.ai", "research", "internal"],
    citedBy: { who: "Researcher", ago: "2 mins ago" },
    kind: "analysis",
  },
  {
    id: "r-3",
    title: "EU AI Act implementation timeline — Reuters Legal",
    domain: "reuters.com",
    url: "reuters.com › legal › ai-act-timeline-2026",
    snippet:
      "Tier-2 obligations enter into force in stages through 2026 and 2027. Member states have begun designating national competent authorities ahead of the May enforcement date…",
    confidence: 0.78,
    provenance: ["reuters.com", "press"],
    kind: "press",
  },
  {
    id: "r-4",
    title: "Practitioner guide: risk-tier 2 controls mapping",
    domain: "iapp.org",
    url: "iapp.org › news › a › ai-act-controls",
    snippet:
      "Mapping internal ISMS controls onto the Annex IV technical documentation requirements. Most teams underestimate the post-market monitoring obligation; the rest is mostly relabelling…",
    confidence: 0.61,
    provenance: ["iapp.org", "analysis"],
    kind: "analysis",
  },
  {
    id: "r-5",
    title: "EU AI Act · Annex IV technical documentation requirements (consolidated)",
    domain: "eur-lex.europa.eu",
    url: "eur-lex.europa.eu › annex-iv › consolidated",
    snippet:
      "Consolidated text of Annex IV obligations covering risk-tier 2 systems: design specifications, training data lineage, evaluation methodology and post-deployment incident reporting…",
    confidence: 0.71,
    provenance: ["eur-lex.europa.eu", "primary"],
    kind: "primary",
  },
];

const KIND_LABEL: Record<Result["kind"], string> = {
  intergov: "Intergov",
  press: "Press",
  primary: "Primary",
  analysis: "Analysis",
};

type Audience = "humans" | "agents" | "llms";

// Partial-fill: query already in the box + top 2 results rendered, so the
// results pane never reads as empty on first paint.
const INITIAL_REVEALED = 2;

export function SearchMock() {
  const [typed, setTyped] = useState(QUERY.length);
  const [revealed, setRevealed] = useState(INITIAL_REVEALED);
  const [showJson, setShowJson] = useState(false);
  const [status, setStatus] = useState<"typing" | "running" | "complete">("running");
  const [audience, setAudience] = useState<Audience>("humans");
  const [latency, setLatency] = useState(124);
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const clearTimers = () => {
      timers.current.forEach((t) => clearTimeout(t));
      timers.current = [];
    };
    const schedule = (fn: () => void, delay: number) => {
      const id = window.setTimeout(fn, delay);
      timers.current.push(id);
    };

    const runCycle = () => {
      // Loop reset = the partial-fill snapshot (query already typed + first 2
      // results visible), never an empty state.
      setTyped(QUERY.length);
      setRevealed(INITIAL_REVEALED);
      setShowJson(false);
      setStatus("running");
      setLatency(80 + Math.floor(Math.random() * 80));

      const typeEnd = 0; // query is already typed at first paint

      // Stream the remaining results (rank 3..N) from the partial-fill point.
      for (let n = INITIAL_REVEALED + 1; n <= RESULTS.length; n += 1) {
        schedule(() => setRevealed(n), typeEnd + 400 + (n - INITIAL_REVEALED) * 450);
      }
      schedule(() => setStatus("complete"), typeEnd + 400 + (RESULTS.length - INITIAL_REVEALED) * 450 + 300);

      // Flip to JSON view briefly to show "agents can also call this"
      const jsonStart = typeEnd + 400 + (RESULTS.length - INITIAL_REVEALED) * 450 + 900;
      schedule(() => {
        setAudience("agents");
        setShowJson(true);
      }, jsonStart);
      schedule(() => {
        setAudience("humans");
        setShowJson(false);
      }, jsonStart + 1800);

      // Loop
      schedule(runCycle, jsonStart + 1800 + 2200);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        schedule(runCycle, 400);
        obs.disconnect();
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      clearTimers();
    };
  }, []);

  return (
    <div ref={ref} className="relative w-full max-w-4xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            search.askfinz.ai · live
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <span
              className={
                "h-1.5 w-1.5 rounded-full " +
                (status === "running"
                  ? "bg-[hsl(var(--accent))] animate-pulse"
                  : status === "complete"
                  ? "bg-foreground/70"
                  : "bg-foreground/30")
              }
            />
            {status === "running" ? "Searching" : status === "complete" ? "Ranked" : "Idle"}
          </span>
        </div>

        {/* Audience tabs — humans / agents / LLMs */}
        <div className="flex items-center gap-1 px-4 py-2 border-b hairline text-[10px] font-mono tracking-[0.14em] uppercase overflow-x-auto">
          <span className="text-foreground/65 mr-2 shrink-0">Audience</span>
          {([
            { id: "humans" as Audience, label: "Humans", icon: User },
            { id: "agents" as Audience, label: "Agents", icon: Bot },
            { id: "llms"   as Audience, label: "LLMs",   icon: Sparkles },
          ]).map((a) => {
            const Icon = a.icon;
            const active = audience === a.id;
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => {
                  setAudience(a.id);
                  setShowJson(a.id !== "humans");
                }}
                className={
                  "inline-flex items-center gap-1.5 rounded-full px-2 py-1 transition shrink-0 " +
                  (active
                    ? "bg-foreground text-background"
                    : "border hairline-strong text-foreground/65 hover:text-foreground/85")
                }
              >
                <Icon className="h-3 w-3" />
                {a.label}
              </button>
            );
          })}
          <span className="ml-auto inline-flex items-center gap-1.5 text-foreground/65 shrink-0 whitespace-nowrap">
            <Globe className="h-3 w-3" />
            EN · {COLLECTION_COUNT}+ collections · <span className="tabular-num text-foreground/65">{latency}ms</span>
          </span>
        </div>

        {/* Query bar */}
        <div className="px-4 py-4 border-b hairline bg-background">
          <div className="flex items-center gap-2.5 rounded-xl border hairline-strong bg-card px-3.5 py-2.5">
            <Search className="h-4 w-4 text-foreground/65 shrink-0" />
            <div className="flex-1 text-[14px] text-foreground/90 font-mono truncate">
              {QUERY.slice(0, typed)}
              {typed < QUERY.length && (
                <span
                  aria-hidden
                  className="inline-block ml-0.5 h-[1em] w-[1.5px] align-middle bg-[hsl(var(--accent))]/80 animate-pulse"
                />
              )}
            </div>
            <span className="text-[10px] font-mono tracking-[0.14em] uppercase text-foreground/65 shrink-0">
              ⌘K
            </span>
          </div>
        </div>

        {/* Results */}
        <div className="px-4 py-4 min-h-[420px] relative">
          {showJson ? (
            <JsonPreview query={QUERY} latency={latency} />
          ) : (
            <div className="space-y-2.5">
              {RESULTS.slice(0, revealed).map((r, i) => (
                <ResultCard key={r.id} r={r} index={i} />
              ))}
              {revealed < RESULTS.length && status === "running" && (
                <div className="px-2 py-3 inline-flex items-center gap-2 text-[11px] font-mono tracking-[0.14em] uppercase text-foreground/65">
                  <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] animate-pulse" />
                  Streaming results · {revealed} / {RESULTS.length}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer strip */}
        <div className="border-t hairline bg-foreground/[0.02] px-4 py-3 flex items-center gap-3 text-[10px] font-mono tracking-[0.14em] uppercase text-foreground/65 overflow-x-auto">
          <FileJson className="h-3.5 w-3.5 shrink-0" />
          <span className="shrink-0 whitespace-nowrap">Built for LLMs and humans</span>
          <span className="text-foreground/30 shrink-0">·</span>
          <span className="shrink-0 whitespace-nowrap">structured output available</span>
          <span className="ml-auto inline-flex items-center gap-1 text-foreground/65 shrink-0 whitespace-nowrap">
            <Code2 className="h-3 w-3" />
            GET /v1/search
            <ChevronRight className="h-3 w-3" />
          </span>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.18em] text-foreground/65">
        <ArrowUpRight className="h-3 w-3" />
        <span>
          Live product at <span className="font-mono text-foreground/65">search.askfinz.ai</span>
        </span>
      </div>
    </div>
  );
}

function ResultCard({ r, index }: { r: Result; index: number }) {
  const ScoreIcon = r.confidence >= 0.8 ? ShieldCheck : ShieldAlert;
  const scoreLabel =
    r.confidence >= 0.85 ? "High" : r.confidence >= 0.7 ? "Strong" : r.confidence >= 0.55 ? "Moderate" : "Low";
  return (
    <div
      className="rounded-xl border hairline bg-background p-3.5 animate-[blur-fade-in_0.45s_ease-out] hover:hairline-strong transition-colors"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <div className="flex items-center gap-2 text-[10px] font-mono tracking-[0.14em] uppercase text-foreground/65">
        <span className="inline-flex h-4 w-4 items-center justify-center rounded-sm border hairline-strong bg-card">
          <Globe className="h-2.5 w-2.5" />
        </span>
        <span className="text-foreground/65">{r.domain}</span>
        <span className="text-foreground/30">›</span>
        <span className="truncate text-foreground/65">{r.url.split(" › ").slice(1).join(" › ")}</span>
        <span className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border hairline-strong text-foreground/65">
          {KIND_LABEL[r.kind]}
        </span>
      </div>

      <div className="mt-2 text-[14px] leading-snug text-foreground/95 font-medium">
        {r.title}
      </div>

      <p className="mt-1.5 text-[12.5px] leading-relaxed text-foreground/65">{r.snippet}</p>

      <div className="mt-3 flex items-center gap-3">
        <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.14em] text-foreground/65">
          <ScoreIcon className="h-3 w-3" />
          <span>Confidence · {scoreLabel}</span>
        </div>
        <div className="flex-1 h-1.5 rounded-full bg-foreground/8 overflow-hidden">
          <div
            className="h-full bg-[hsl(var(--accent))]/85 rounded-full"
            style={{
              width: `${Math.round(r.confidence * 100)}%`,
              animation: "blur-fade-in 0.6s ease-out",
            }}
          />
        </div>
        <span className="text-[10px] font-mono tabular-num text-foreground/65 w-10 text-right">
          {Math.round(r.confidence * 100)}%
        </span>
      </div>

      {r.citedBy && (
        <div className="mt-2.5 inline-flex items-center gap-1.5 rounded-md border hairline-strong bg-[hsl(var(--accent))]/8 px-2 py-1 text-[10px] font-mono tracking-[0.14em] uppercase">
          <Bot className="h-3 w-3 text-[hsl(var(--accent))]" />
          <span className="text-foreground/80">Cited by {r.citedBy.who}</span>
          <span className="text-foreground/65">· {r.citedBy.ago}</span>
        </div>
      )}
    </div>
  );
}

function JsonPreview({ query, latency }: { query: string; latency: number }) {
  return (
    <div className="rounded-xl border hairline-strong bg-foreground/[0.03] p-4 font-mono text-[11.5px] text-foreground/80 leading-[1.7] overflow-hidden animate-[blur-fade-in_0.4s_ease-out]">
      <div className="flex items-center gap-2 text-[10px] tracking-[0.14em] uppercase text-foreground/65 mb-2">
        <FileJson className="h-3 w-3" />
        Same query · structured for agents and LLMs
      </div>
      <pre className="whitespace-pre-wrap">
{`POST /v1/search
{
  "q": "${query}",
  "audience": "agent",
  "limit": 4,
  "include": ["confidence", "provenance", "snippet"]
}

→ 200 OK · 4 results · ${latency} ms
[
  { "rank": 1, "title": "EU AI Act · Art. 9 · Risk management…",
    "confidence": 0.94, "provenance": ["eur-lex.europa.eu"], "kind": "primary" },
  { "rank": 2, "title": "Tier-2 obligations explainer …",
    "confidence": 0.86, "cited_by": ["agent:researcher"], "kind": "analysis" },
  …
]`}
      </pre>
    </div>
  );
}

export default SearchMock;
